import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VerificationMethod, VerifiedResultPayload } from '@/types';
import { PUBLIC_STATUS_KEY, publicService, type PublicStatus } from '@/services/publicService';
import VerificationForm, { type VerificationInput } from './components/VerificationForm';
import ResultVerified from './components/ResultVerified';
import ResultFailed from './components/ResultFailed';
import ResultRateLimited from './components/ResultRateLimited';
import Spinner from '@/components/base/Spinner';

type Phase = 'form' | 'loading' | 'verified' | 'failed' | 'rate_limited';

const MAINTENANCE_MESSAGE = 'Verification is temporarily unavailable. Please try again later.';

const METHODS: VerificationMethod[] = ['matric', 'certificate', 'result'];

/**
 * What a pre-filled link puts in the form, e.g. a university's
 * STATEMENT_VERIFY_URL with GradFlow's method=result&reference=<code> added.
 * Nothing is checked until the verifier presses Verify.
 */
function fromLink(params: URLSearchParams): VerificationInput | null {
  const institutionId = params.get('institution')?.trim() ?? '';
  const method = params.get('method') as VerificationMethod | null;
  const reference = params.get('reference')?.trim().slice(0, 64) ?? '';
  if (!institutionId && !method && !reference) return null;
  return { institutionId, method: method && METHODS.includes(method) ? method : 'matric', reference };
}

export default function Home() {
  const institutionsQuery = useQuery({
    queryKey: ['public', 'institutions'],
    queryFn: publicService.institutions,
    staleTime: 60_000,
  });
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: PUBLIC_STATUS_KEY,
    queryFn: publicService.status,
    staleTime: 60_000,
    // While maintenance is on, check every minute so the form reopens by itself.
    refetchInterval: (query) => (query.state.data?.maintenanceMode ? 60_000 : false),
    refetchOnWindowFocus: (query) => query.state.data?.maintenanceMode === true,
  });

  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('form');
  const [verified, setVerified] = useState<VerifiedResultPayload | null>(null);
  const [failedMessage, setFailedMessage] = useState('');
  const [failedVerificationId, setFailedVerificationId] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [lastInput, setLastInput] = useState<VerificationInput | null>(() => fromLink(searchParams));
  const [formError, setFormError] = useState('');

  const maintenance = statusQuery.data?.maintenanceMode === true;

  async function handleVerify(institutionId: string, method: VerificationMethod, reference: string) {
    setLastInput({ institutionId, method, reference });
    setFormError('');
    setPhase('loading');
    setVerified(null);
    setFailedMessage('');
    setFailedVerificationId(null);

    try {
      const outcome = await publicService.verify(institutionId, method, reference);
      switch (outcome.status) {
        case 'verified':
          setVerified(outcome.data);
          setPhase('verified');
          break;
        case 'not_verified':
        case 'error':
          setFailedMessage(outcome.message);
          setFailedVerificationId(outcome.verificationId);
          setPhase('failed');
          break;
        case 'invalid':
          setFormError(outcome.message);
          setPhase('form');
          break;
        case 'rate_limited':
          setRetryAfter(outcome.retryAfter);
          setPhase('rate_limited');
          break;
        case 'maintenance':
          queryClient.setQueryData<PublicStatus>(PUBLIC_STATUS_KEY, (old) => ({ ...old, maintenanceMode: true }));
          setPhase('form');
          break;
      }
    } catch (error) {
      setFailedMessage(error instanceof Error ? error.message : 'Something went wrong.');
      setPhase('failed');
    }
  }

  function reset() {
    setPhase('form');
    setVerified(null);
    setFailedMessage('');
    setFailedVerificationId(null);
    setFormError('');
    setLastInput(null);
  }

  /** Back to the form with what was entered, e.g. after waiting out a rate limit. */
  function backToForm() {
    setPhase('form');
    setFormError('');
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Header */}
      <header className="border-b border-navy-100">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 md:px-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy-900 text-white">
            <i className="ri-shield-check-line text-xl" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold leading-tight text-navy-900">
              Certify
            </h1>
            <p className="text-xs text-navy-500">Secure Certificate Verification</p>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex flex-1 items-start justify-center px-4 py-8 md:py-14">
        <div className="w-full max-w-2xl">
          <div className="mb-6 text-center">
            <h2 className="font-heading text-2xl font-bold text-navy-900 md:text-3xl">
              Verify a Certificate or Result
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-navy-500">
              Select the issuing institution and enter the matriculation number, certificate code,
              or the verification code on a Statement of Result.
            </p>
          </div>

          {maintenance && (
            <div
              role="status"
              className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              <i className="ri-tools-line mt-0.5 text-base" />
              <span>{MAINTENANCE_MESSAGE}</span>
            </div>
          )}

          <div className="rounded-xl border border-navy-100 bg-white p-6 shadow-sm md:p-8">
            {phase === 'form' && (
              <VerificationForm
                institutions={institutionsQuery.data ?? []}
                institutionsLoading={institutionsQuery.isPending}
                institutionsError={
                  institutionsQuery.isError ? 'Could not load institutions.' : undefined
                }
                onRetryInstitutions={() => institutionsQuery.refetch()}
                onSubmit={handleVerify}
                loading={false}
                disabled={maintenance}
                initialValues={lastInput}
                serverError={formError}
              />
            )}

            {phase === 'loading' && (
              <div className="flex flex-col items-center gap-4 py-14 text-center">
                <Spinner size="lg" />
                <p className="text-sm text-navy-600">
                  {lastInput?.method === 'result' ? 'Verifying Statement of Result…' : 'Verifying certificate…'}
                </p>
                <p className="text-xs text-navy-400">
                  Contacting the institution's verification service
                </p>
              </div>
            )}

            {phase === 'verified' && verified && (
              <ResultVerified data={verified} onReset={reset} />
            )}

            {phase === 'failed' && (
              <ResultFailed
                message={failedMessage}
                verificationId={failedVerificationId}
                method={lastInput?.method}
                onRetry={reset}
              />
            )}

            {phase === 'rate_limited' && (
              <ResultRateLimited retryAfter={retryAfter} onBack={backToForm} />
            )}
          </div>

          <p className="mt-6 text-center text-xs text-navy-400">
            No account or login is required to verify a certificate.
          </p>
          <p className="mt-2 text-center text-xs text-navy-400">
            Is your university on GradFlow?{' '}
            <Link to="/join" className="font-medium text-navy-600 underline hover:text-navy-800">
              Request to join Certify
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-navy-100 py-5">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-2 px-4 text-xs text-navy-400 sm:flex-row md:px-6">
          <span>© 2026 Certify</span>
          <span className="flex items-center gap-1.5">
            <i className="ri-shield-check-line" />
            Powered by Emchon
          </span>
        </div>
      </footer>
    </div>
  );
}